import { useState } from 'react';

import type { FormEncType } from '@remix-run/react';

type UnwrapPromise<T> = T extends Promise<infer U> ? U : T;

export function useNonRevalidatingFetcher<T extends (args: unknown) => Promise<unknown>>() {
  type Unwrapped = UnwrapPromise<ReturnType<T>>;

  const [state, setState] = useState<'idle' | 'loading' | 'submitting'>('idle');
  const [data, setData] = useState<Unwrapped | undefined>(undefined);

  const load = async (url: string) => {
    const abortController = new AbortController();
    setState('loading');
    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const result = await response.json();
      handleComponentError(result);
      setData(result);
    } catch (error) {
      console.error('Fetch error:', error);
      setData(undefined); // Reset data on error
    } finally {
      setState('idle');
    }
    return () => {
      abortController.abort(); // Cleanup on unmount
    };
  };

  const submit = async (body: BodyInit, options: RequestInit & { action?: string; encType?: FormEncType }) => {
    setState('submitting');
    try {
      const response = await fetch(options?.action ?? '.', { ...options, body });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const result = await response.json();
      handleComponentError(result);
      setData(result);
    } catch (error) {
      console.error('Fetch error:', error);
      setData(undefined); // Reset data on error
    } finally {
      setState('idle');
    }
  };

  return { state, data, load, submit };
}

function handleComponentError<T>(fetcherData: T) {
  if (fetcherData && typeof fetcherData === 'object' && '$$error' in fetcherData) {
    throw fetcherData?.$$error;
  }
}
